
import { Link } from '@tanstack/react-router';
import { useLocation } from '@tanstack/react-router';

import { cn } from '@/lib/cn';

/** A named run of screens. See `groupNav` for why an empty one is dropped. */
/**
 * Whether a menu link is the page you are on. A whole path segment must match:
 * `/admin/stock-takes` starts with `/admin/stock`, and a plain prefix test lit
 * "Stock on hand" and "Stocktakes" together.
 */
function isActive(pathname: string, href: string): boolean {
  if (href === '/admin') return pathname === '/admin';
  return pathname === href || pathname.startsWith(`${href}/`);
}

export interface AdminNavGroup {
  name: string;
  items: AdminNavItem[];
}

export interface AdminNavItem {
  href: string;
  label: string;
  count?: number;
}

/**
 * `roomy` is the phone and tablet panel (tracker #8). The sidebar's 28 px rows
 * suit a mouse; a thumb needs 44 px, and 15 px text is readable at arm's length.
 */
export function AdminNav({ groups, roomy = false }: { groups: AdminNavGroup[]; roomy?: boolean }) {
  const pathname = useLocation().pathname;

  return (
    <nav aria-label="Admin">
      <div className="space-y-5">
        {groups.map((group) => (
          <div key={group.name}>
            {/* Mono micro-label, one of the two places uppercase survives. */}
            <h2 className="mb-1.5 px-2.5 font-[family-name:var(--font-mono)] text-[10px] font-medium uppercase tracking-[0.08em] text-faint">
              {group.name}
            </h2>
            <ul className="space-y-px">
              {group.items.map((item) => {
          const active = isActive(pathname, item.href);
          return (
            <li key={item.href}>
              <Link
                to={item.href}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  // 28px rows, 13px, weight 500. The selected row is a filled
                  // block in ink rather than a yellow wash: the back office has
                  // no chromatic accent at all now. R-187.
                  'flex items-center justify-between gap-2 rounded-[6px] px-2.5 font-medium transition-colors duration-150',
                  roomy ? 'h-11 text-[15px]' : 'h-7 text-[13px]',
                  active
                    ? 'bg-[color:var(--color-control)] text-[color:var(--color-on-control)]'
                    : 'text-ink-2 hover:bg-deep hover:text-ink',
                )}
              >
                {item.label}
                {item.count != null && item.count > 0 && (
                  <span className={cn(
                    'grid min-w-[18px] place-items-center rounded-[4px] px-1 font-[family-name:var(--font-mono)] text-[10px] font-medium tabular-nums',
                    active
                      ? 'bg-[color:var(--color-on-control)]/20 text-[color:var(--color-on-control)]'
                      : 'bg-deep text-muted',
                  )}>
                    {item.count}
                  </span>
                )}
              </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}
